import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { v2API, cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, Download, History, Sparkles, Mail, Eye, 
  Plus, Trash2, Save, ArrowLeft, Upload, RefreshCw,
  FileDown, Search, Calendar, User, Loader2, Camera, X,
  ChevronUp, ChevronDown, Wand2, Award
} from 'lucide-react';

const CVBuilderV2 = () => {
  const { cvId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // View mode: 'list' | 'create' | 'edit' - derive from cvId
  const [viewMode, setViewMode] = useState('list');
  
  // Sync viewMode with cvId from URL
  useEffect(() => {
    if (cvId) {
      setViewMode('edit');
    } else if (location.pathname === '/cv-builder-v2') {
      setViewMode('list');
    }
  }, [cvId, location.pathname]);
  
  // CV List state
  const [cvList, setCvList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [listLoading, setListLoading] = useState(false);
  
  // Core state
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('v2-modern-minimal');
  const [activeTab, setActiveTab] = useState('edit');
  
  // Generation state (for new CV)
  const [rawText, setRawText] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [language, setLanguage] = useState('en');
  const [generating, setGenerating] = useState(false);
  
  // Edit state
  const [structuredCv, setStructuredCv] = useState(null);
  const [sectionVisibility, setSectionVisibility] = useState({
    summary: true, experience: true, education: true,
    skills: true, certifications: true, languages: true,
    projects: true, references: true, awards: true, customSections: true
  });
  
  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  
  // Task generation state
  const [generatingTasksFor, setGeneratingTasksFor] = useState(null); // Index of experience being generated for
  
  // Smart Edit state
  const [smartEditContent, setSmartEditContent] = useState('');
  const [smartEditMode, setSmartEditMode] = useState('conservative');
  const [smartEditLoading, setSmartEditLoading] = useState(false);
  
  // History state
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Cover Letter state
  const [coverLetter, setCoverLetter] = useState(null);
  const [coverLetterForm, setCoverLetterForm] = useState({
    jobTitle: '',
    companyName: '',
    jobDescription: '',
    tone: 'professional'
  });
  const [generatingLetter, setGeneratingLetter] = useState(false);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      // Let React Router handle the back navigation naturally
      // The viewMode will update based on cvId changes
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await v2API.getTemplates();
        setTemplates(data.templates || []);
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    };
    loadTemplates();
  }, []);

  // Load CV list
  useEffect(() => {
    if (viewMode === 'list') {
      loadCVList();
    }
  }, [viewMode, searchQuery]);

  // Load CV if editing
  useEffect(() => {
    if (cvId) {
      loadCV();
      loadVersions();
    } else {
      // Reset state when navigating back to list
      setCv(null);
      setStructuredCv(null);
      setVersions([]);
    }
  }, [cvId]);

  const loadCVList = async () => {
    setListLoading(true);
    try {
      const data = await v2API.list(searchQuery);
      setCvList(data || []);
    } catch (error) {
      console.error('Failed to load CVs:', error);
      toast.error('Failed to load CVs');
    } finally {
      setListLoading(false);
    }
  };

  const loadCV = async () => {
    setLoading(true);
    try {
      const data = await v2API.get(cvId);
      setCv(data);
      setStructuredCv(data.structuredCv);
      setSelectedTemplate(data.v2TemplateId);
      setLanguage(data.language);
      if (data.sectionVisibility) {
        setSectionVisibility(data.sectionVisibility);
      }
    } catch (error) {
      toast.error('Failed to load CV');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      const data = await v2API.getVersions(cvId);
      setVersions(data);
    } catch (error) {
      console.error('Failed to load versions:', error);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (viewMode === 'edit' || viewMode === 'create') {
      navigate('/cv-builder-v2');
    }
  };

  // Generate new CV
  const handleGenerate = async () => {
    if (!rawText.trim() || !candidateName.trim()) {
      toast.error('Please enter candidate name and CV content');
      return;
    }
    
    setGenerating(true);
    try {
      const data = await v2API.generate({
        candidateName,
        rawText,
        instructions: '',
        language,
        v2TemplateId: selectedTemplate,
        photoUrl: ''
      });
      
      toast.success('CV generated successfully!');
      navigate(`/cv-builder-v2/${data.id}`);
    } catch (error) {
      toast.error('Failed to generate CV');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setPhotoUploading(true);
    try {
      const result = await cvAPI.uploadPhoto(file);
      
      // Update the structured CV with the photo URL
      const updatedStructuredCv = {
        ...structuredCv,
        candidate: {
          ...structuredCv.candidate,
          photoUrl: result.photoUrl
        }
      };
      setStructuredCv(updatedStructuredCv);
      
      // Auto-save after photo upload
      if (cvId) {
        const data = await v2API.update(cvId, {
          structuredCv: updatedStructuredCv,
          v2TemplateId: selectedTemplate,
          language,
          updateNote: 'Photo uploaded'
        });
        setCv(data);
        toast.success('Photo uploaded and saved!');
        loadVersions();
      } else {
        toast.success('Photo uploaded! Click Save to apply.');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error(error);
    } finally {
      setPhotoUploading(false);
    }
  };

  // Remove photo
  const handleRemovePhoto = async () => {
    const updatedStructuredCv = {
      ...structuredCv,
      candidate: {
        ...structuredCv.candidate,
        photoUrl: ''
      }
    };
    setStructuredCv(updatedStructuredCv);
    
    // Auto-save after photo removal
    if (cvId) {
      try {
        const data = await v2API.update(cvId, {
          structuredCv: updatedStructuredCv,
          v2TemplateId: selectedTemplate,
          language,
          updateNote: 'Photo removed'
        });
        setCv(data);
        toast.success('Photo removed and saved!');
        loadVersions();
      } catch (error) {
        toast.error('Failed to save');
        console.error(error);
      }
    } else {
      toast.success('Photo removed');
    }
  };

  // Save CV
  const handleSave = async () => {
    if (!cvId || !structuredCv) return;
    
    setSaving(true);
    try {
      const data = await v2API.update(cvId, {
        structuredCv,
        v2TemplateId: selectedTemplate,
        language,
        updateNote: 'Manual edit'
      });
      
      setCv(data);
      toast.success('CV saved!');
      loadVersions();
    } catch (error) {
      toast.error('Failed to save CV');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Smart Edit
  const handleSmartEdit = async () => {
    if (!smartEditContent.trim()) {
      toast.error('Please paste content to apply');
      return;
    }
    
    setSmartEditLoading(true);
    try {
      const result = await v2API.smartEdit(cvId, {
        cvId,
        pastedContent: smartEditContent,
        conservativeMode: smartEditMode === 'conservative',
        replaceMode: smartEditMode === 'replace'
      });
      
      if (result.success) {
        toast.success(`Applied ${result.changes.length} changes`);
        setStructuredCv(result.structuredCv);
        setCv(prev => ({ ...prev, htmlSnapshot: result.htmlSnapshot }));
        setSmartEditContent('');
        loadVersions();
      } else {
        toast.error(result.error || 'Smart edit failed');
      }
    } catch (error) {
      toast.error('Smart edit failed');
      console.error(error);
    } finally {
      setSmartEditLoading(false);
    }
  };

  // Restore version
  const handleRestoreVersion = async (versionId) => {
    try {
      const result = await v2API.restoreVersion(cvId, versionId);
      if (result.success) {
        toast.success(`Restored to version ${result.restoredToVersion}`);
        setStructuredCv(result.structuredCv);
        setCv(prev => ({ ...prev, htmlSnapshot: result.htmlSnapshot }));
        loadVersions();
      }
    } catch (error) {
      toast.error('Failed to restore version');
      console.error(error);
    }
  };

  // Generate Cover Letter
  const handleGenerateCoverLetter = async () => {
    if (!coverLetterForm.jobTitle) {
      toast.error('Job title is required');
      return;
    }
    
    setGeneratingLetter(true);
    try {
      const result = await v2API.generateCoverLetter({
        cvId,
        ...coverLetterForm,
        language
      });
      
      setCoverLetter(result);
      toast.success('Cover letter generated!');
    } catch (error) {
      toast.error('Failed to generate cover letter');
      console.error(error);
    } finally {
      setGeneratingLetter(false);
    }
  };

  // Update section visibility
  const toggleSectionVisibility = async (section) => {
    const newVisibility = { ...sectionVisibility, [section]: !sectionVisibility[section] };
    setSectionVisibility(newVisibility);
    
    if (cvId) {
      try {
        await v2API.updateVisibility(cvId, newVisibility);
        loadCV();
      } catch (error) {
        console.error('Failed to update visibility:', error);
      }
    }
  };

  // Update structured CV field
  const updateField = (path, value) => {
    setStructuredCv(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        if (keys[i].includes('[')) {
          const [key, index] = keys[i].split('[');
          current = current[key][parseInt(index.replace(']', ''))];
        } else {
          current = current[keys[i]];
        }
      }
      const lastKey = keys[keys.length - 1];
      if (lastKey.includes('[')) {
        const [key, index] = lastKey.split('[');
        current[key][parseInt(index.replace(']', ''))] = value;
      } else {
        current[lastKey] = value;
      }
      return updated;
    });
  };

  // Add/Remove/Update experience
  const addExperience = () => {
    setStructuredCv(prev => ({
      ...prev,
      experience: [...(prev.experience || []), {
        role: '', company: '', location: '', start: '', end: '', description: '', bullets: []
      }]
    }));
  };

  const removeExperience = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
  };

  // Move experience up
  const moveExperienceUp = (index) => {
    if (index === 0) return;
    setStructuredCv(prev => {
      const newExperience = [...prev.experience];
      [newExperience[index - 1], newExperience[index]] = [newExperience[index], newExperience[index - 1]];
      return { ...prev, experience: newExperience };
    });
  };

  // Move experience down
  const moveExperienceDown = (index) => {
    setStructuredCv(prev => {
      if (index >= prev.experience.length - 1) return prev;
      const newExperience = [...prev.experience];
      [newExperience[index], newExperience[index + 1]] = [newExperience[index + 1], newExperience[index]];
      return { ...prev, experience: newExperience };
    });
  };

  // Generate tasks using AI
  const generateTasks = async (expIndex) => {
    const exp = structuredCv.experience[expIndex];
    if (!exp.role) {
      toast.error('Please enter a job title first');
      return;
    }
    
    setGeneratingTasksFor(expIndex);
    try {
      const result = await v2API.generateTasks(exp.role, exp.company);
      
      if (result.success && result.tasks) {
        // Add generated tasks to the experience
        setStructuredCv(prev => {
          const updated = { ...prev };
          updated.experience = [...updated.experience];
          updated.experience[expIndex] = {
            ...updated.experience[expIndex],
            bullets: [...(updated.experience[expIndex].bullets || []), ...result.tasks]
          };
          return updated;
        });
        toast.success(`Generated ${result.tasks.length} tasks!`);
      }
    } catch (error) {
      toast.error('Failed to generate tasks');
      console.error(error);
    } finally {
      setGeneratingTasksFor(null);
    }
  };

  // Add/Remove certifications
  const addCertification = () => {
    setStructuredCv(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), {
        name: '', issuer: '', year: ''
      }]
    }));
  };

  const removeCertification = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  // Add bullet to experience
  const addBullet = (expIndex) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.experience = [...updated.experience];
      updated.experience[expIndex] = {
        ...updated.experience[expIndex],
        bullets: [...(updated.experience[expIndex].bullets || []), '']
      };
      return updated;
    });
  };

  // Update bullet
  const updateBullet = (expIndex, bulletIndex, value) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.experience = [...updated.experience];
      updated.experience[expIndex] = {
        ...updated.experience[expIndex],
        bullets: [...updated.experience[expIndex].bullets]
      };
      updated.experience[expIndex].bullets[bulletIndex] = value;
      return updated;
    });
  };

  // Remove bullet
  const removeBullet = (expIndex, bulletIndex) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.experience = [...updated.experience];
      updated.experience[expIndex] = {
        ...updated.experience[expIndex],
        bullets: updated.experience[expIndex].bullets.filter((_, i) => i !== bulletIndex)
      };
      return updated;
    });
  };

  // Add/Remove education
  const addEducation = () => {
    setStructuredCv(prev => ({
      ...prev,
      education: [...(prev.education || []), {
        school: '', degree: '', start: '', end: '', notes: [], gpa: ''
      }]
    }));
  };

  const removeEducation = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  // Custom sections management
  const addCustomSection = () => {
    setStructuredCv(prev => ({
      ...prev,
      customSections: [...(prev.customSections || []), {
        title: 'New Section',
        items: ['']
      }]
    }));
  };

  const removeCustomSection = (index) => {
    setStructuredCv(prev => ({
      ...prev,
      customSections: prev.customSections.filter((_, i) => i !== index)
    }));
  };

  const updateCustomSectionTitle = (index, title) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.customSections = [...updated.customSections];
      updated.customSections[index] = { ...updated.customSections[index], title };
      return updated;
    });
  };

  const addCustomSectionItem = (sectionIndex) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.customSections = [...updated.customSections];
      updated.customSections[sectionIndex] = {
        ...updated.customSections[sectionIndex],
        items: [...updated.customSections[sectionIndex].items, '']
      };
      return updated;
    });
  };

  const updateCustomSectionItem = (sectionIndex, itemIndex, value) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.customSections = [...updated.customSections];
      updated.customSections[sectionIndex] = {
        ...updated.customSections[sectionIndex],
        items: [...updated.customSections[sectionIndex].items]
      };
      updated.customSections[sectionIndex].items[itemIndex] = value;
      return updated;
    });
  };

  const removeCustomSectionItem = (sectionIndex, itemIndex) => {
    setStructuredCv(prev => {
      const updated = { ...prev };
      updated.customSections = [...updated.customSections];
      updated.customSections[sectionIndex] = {
        ...updated.customSections[sectionIndex],
        items: updated.customSections[sectionIndex].items.filter((_, i) => i !== itemIndex)
      };
      return updated;
    });
  };

  // Delete CV
  const handleDeleteCV = async (id) => {
    if (!window.confirm('Are you sure you want to delete this CV?')) return;
    
    try {
      await v2API.delete(id);
      toast.success('CV deleted');
      loadCVList();
    } catch (error) {
      toast.error('Failed to delete CV');
    }
  };

  // ============ RENDER: CV LIST VIEW ============
  if (viewMode === 'list') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">CV Builder V2</h1>
              <p className="text-gray-500 mt-1">Create and manage professional CVs with modern templates</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
              <Button 
                onClick={() => setViewMode('create')} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-new-v2-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Create New CV
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-gray-200"
                data-testid="v2-search-input"
              />
            </div>
          </div>

          {/* CV List */}
          {listLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : cvList.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-200 bg-white/50">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <FileText className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-medium text-gray-700 mb-2">
                  {searchQuery ? 'No CVs found' : 'No V2 CVs yet'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery ? 'Try a different search term' : 'Create your first CV with the new V2 builder'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setViewMode('create')} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Create Your First V2 CV
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cvList.map((item) => (
                <Card 
                  key={item.id} 
                  className="bg-white hover:shadow-lg transition-all cursor-pointer border-gray-200 group"
                  onClick={() => navigate(`/cv-builder-v2/${item.id}`)}
                  data-testid={`v2-cv-card-${item.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCV(item.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-lg mb-1">{item.candidateName || 'Unnamed'}</h3>
                    <p className="text-gray-500 text-sm mb-3">{item.candidateTitle || 'No title'}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {templates.find(t => t.id === item.v2TemplateId)?.name || item.v2TemplateId}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(item.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ RENDER: CREATE NEW CV VIEW ============
  if (viewMode === 'create') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-6xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New CV</h1>
              <p className="text-gray-500 mt-1">Generate a professional CV from your information</p>
            </div>
            <Button variant="outline" onClick={() => setViewMode('list')} data-testid="back-list-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to List
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Input Form */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  CV Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-700">Candidate Name *</Label>
                  <Input
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="John Doe"
                    className="bg-gray-50 border-gray-200"
                    data-testid="candidate-name-input"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-700">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="bg-gray-50 border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-700">CV Content *</Label>
                  <Textarea
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    placeholder="Paste your CV content, LinkedIn profile, or any professional information here..."
                    className="bg-gray-50 border-gray-200 min-h-[300px]"
                    data-testid="cv-content-textarea"
                  />
                </div>
                
                <Button 
                  onClick={handleGenerate} 
                  disabled={generating || !rawText.trim() || !candidateName.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-cv-btn"
                >
                  {generating ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate CV</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Right: Template Selection */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900">Select Template</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-3 flex items-center justify-center">
                        <FileText className="w-10 h-10 text-gray-400" />
                      </div>
                      <p className="text-gray-900 font-medium text-sm">{template.name}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {template.hasPhoto ? 'With Photo' : 'No Photo'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ============ RENDER: EDIT CV VIEW ============
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        {/* Left Panel - Editor */}
        <div className="w-[45%] flex flex-col border-r border-gray-200 bg-white overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={handleBack} data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <h1 className="text-xl font-bold text-gray-900">CV Builder V2</h1>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowHistory(!showHistory)}
                  data-testid="history-btn"
                >
                  <History className="w-4 h-4 mr-1" /> History
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          </div>

          {/* Editor Content */}
          <div className="flex-1 overflow-y-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
              <TabsList className="w-full justify-start p-2 bg-gray-50 border-b border-gray-200">
                <TabsTrigger value="edit" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <FileText className="w-4 h-4 mr-1" /> Edit
                </TabsTrigger>
                <TabsTrigger value="smart" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <Sparkles className="w-4 h-4 mr-1" /> Smart Edit
                </TabsTrigger>
                <TabsTrigger value="template" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <Eye className="w-4 h-4 mr-1" /> Template
                </TabsTrigger>
                <TabsTrigger value="cover" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <Mail className="w-4 h-4 mr-1" /> Cover Letter
                </TabsTrigger>
              </TabsList>

              {/* Edit Tab */}
              <TabsContent value="edit" className="p-4 space-y-6">
                {structuredCv && (
                  <>
                    {/* Photo Upload Section */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                          <Camera className="w-4 h-4" /> Profile Photo
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4">
                          {structuredCv.candidate?.photoUrl ? (
                            <div className="relative">
                              <img 
                                src={structuredCv.candidate.photoUrl} 
                                alt="Profile" 
                                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                              />
                              <button
                                onClick={handleRemovePhoto}
                                className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                              <User className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoUpload}
                              className="hidden"
                              data-testid="photo-upload-input"
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={photoUploading}
                              data-testid="upload-photo-btn"
                            >
                              {photoUploading ? (
                                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Uploading...</>
                              ) : (
                                <><Upload className="w-4 h-4 mr-1" /> Upload Photo</>
                              )}
                            </Button>
                            <p className="text-xs text-gray-500 mt-2">
                              Recommended: Square image, max 5MB. Used in photo templates.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Personal Info */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          Personal Info
                          <Switch
                            checked={sectionVisibility.summary}
                            onCheckedChange={() => toggleSectionVisibility('summary')}
                          />
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Input
                          value={structuredCv.candidate?.name || ''}
                          onChange={(e) => updateField('candidate.name', e.target.value)}
                          placeholder="Full Name"
                          className="bg-gray-50 border-gray-200"
                        />
                        <Input
                          value={structuredCv.candidate?.title || ''}
                          onChange={(e) => updateField('candidate.title', e.target.value)}
                          placeholder="Job Title"
                          className="bg-gray-50 border-gray-200"
                        />
                        <Textarea
                          value={structuredCv.candidate?.summary || ''}
                          onChange={(e) => updateField('candidate.summary', e.target.value)}
                          placeholder="Professional Summary"
                          className="bg-gray-50 border-gray-200"
                          rows={3}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={structuredCv.contact?.email || ''}
                            onChange={(e) => updateField('contact.email', e.target.value)}
                            placeholder="Email"
                            className="bg-gray-50 border-gray-200"
                          />
                          <Input
                            value={structuredCv.contact?.phone || ''}
                            onChange={(e) => updateField('contact.phone', e.target.value)}
                            placeholder="Phone"
                            className="bg-gray-50 border-gray-200"
                          />
                        </div>
                        <Input
                          value={structuredCv.contact?.address || ''}
                          onChange={(e) => updateField('contact.address', e.target.value)}
                          placeholder="Location"
                          className="bg-gray-50 border-gray-200"
                        />
                      </CardContent>
                    </Card>

                    {/* Experience */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          Experience
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={sectionVisibility.experience}
                              onCheckedChange={() => toggleSectionVisibility('experience')}
                            />
                            <Button size="sm" variant="ghost" onClick={addExperience} data-testid="add-experience-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {structuredCv.experience?.map((exp, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                            {/* Header with reorder buttons */}
                            <div className="flex justify-between items-start gap-2">
                              <div className="flex items-center gap-1">
                                <div className="flex flex-col">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => moveExperienceUp(idx)}
                                    disabled={idx === 0}
                                    className="h-5 w-5 p-0"
                                    data-testid={`move-exp-up-${idx}`}
                                  >
                                    <ChevronUp className="w-3 h-3" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => moveExperienceDown(idx)}
                                    disabled={idx >= (structuredCv.experience?.length || 0) - 1}
                                    className="h-5 w-5 p-0"
                                    data-testid={`move-exp-down-${idx}`}
                                  >
                                    <ChevronDown className="w-3 h-3" />
                                  </Button>
                                </div>
                                <span className="text-xs text-gray-400 font-medium">#{idx + 1}</span>
                              </div>
                              <Input
                                value={exp.role || ''}
                                onChange={(e) => updateField(`experience[${idx}].role`, e.target.value)}
                                placeholder="Job Title"
                                className="bg-white border-gray-200 flex-1"
                              />
                              <Button size="sm" variant="ghost" onClick={() => removeExperience(idx)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                            <Input
                              value={exp.company || ''}
                              onChange={(e) => updateField(`experience[${idx}].company`, e.target.value)}
                              placeholder="Company"
                              className="bg-white border-gray-200"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={exp.start || ''}
                                onChange={(e) => updateField(`experience[${idx}].start`, e.target.value)}
                                placeholder="Start Date"
                                className="bg-white border-gray-200"
                              />
                              <Input
                                value={exp.end || ''}
                                onChange={(e) => updateField(`experience[${idx}].end`, e.target.value)}
                                placeholder="End Date"
                                className="bg-white border-gray-200"
                              />
                            </div>
                            
                            {/* Bullet Points / Tasks */}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex justify-between items-center mb-2">
                                <Label className="text-xs text-gray-600">Key Responsibilities / Tasks</Label>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => generateTasks(idx)}
                                    disabled={generatingTasksFor === idx}
                                    className="h-6 px-2 text-purple-600 border-purple-200 hover:bg-purple-50"
                                    data-testid={`generate-tasks-${idx}`}
                                  >
                                    {generatingTasksFor === idx ? (
                                      <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                                    ) : (
                                      <><Wand2 className="w-3 h-3 mr-1" /> AI Generate</>
                                    )}
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => addBullet(idx)}
                                    className="h-6 px-2"
                                    data-testid={`add-bullet-${idx}`}
                                  >
                                    <Plus className="w-3 h-3 mr-1" /> Add
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-2">
                                {(exp.bullets || []).map((bullet, bulletIdx) => (
                                  <div key={bulletIdx} className="flex gap-2">
                                    <span className="text-gray-400 mt-2">•</span>
                                    <Input
                                      value={bullet}
                                      onChange={(e) => updateBullet(idx, bulletIdx, e.target.value)}
                                      placeholder="Describe a key responsibility or achievement..."
                                      className="bg-white border-gray-200 flex-1"
                                      data-testid={`bullet-${idx}-${bulletIdx}`}
                                    />
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => removeBullet(idx, bulletIdx)}
                                      className="h-9 px-2"
                                    >
                                      <X className="w-3 h-3 text-red-500" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Education */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          Education
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={sectionVisibility.education}
                              onCheckedChange={() => toggleSectionVisibility('education')}
                            />
                            <Button size="sm" variant="ghost" onClick={addEducation} data-testid="add-education-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {structuredCv.education?.map((edu, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                            <div className="flex justify-between items-start">
                              <Input
                                value={edu.degree || ''}
                                onChange={(e) => updateField(`education[${idx}].degree`, e.target.value)}
                                placeholder="Degree"
                                className="bg-white border-gray-200"
                              />
                              <Button size="sm" variant="ghost" onClick={() => removeEducation(idx)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                            <Input
                              value={edu.school || ''}
                              onChange={(e) => updateField(`education[${idx}].school`, e.target.value)}
                              placeholder="School"
                              className="bg-white border-gray-200"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={edu.start || ''}
                                onChange={(e) => updateField(`education[${idx}].start`, e.target.value)}
                                placeholder="Start Year"
                                className="bg-white border-gray-200"
                              />
                              <Input
                                value={edu.end || ''}
                                onChange={(e) => updateField(`education[${idx}].end`, e.target.value)}
                                placeholder="End Year"
                                className="bg-white border-gray-200"
                              />
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Skills */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          Skills
                          <Switch
                            checked={sectionVisibility.skills}
                            onCheckedChange={() => toggleSectionVisibility('skills')}
                          />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={(structuredCv.skills?.all || []).join(', ')}
                          onChange={(e) => updateField('skills.all', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="Python, JavaScript, React, Node.js..."
                          className="bg-gray-50 border-gray-200"
                          rows={2}
                        />
                      </CardContent>
                    </Card>

                    {/* Languages */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          Languages
                          <Switch
                            checked={sectionVisibility.languages}
                            onCheckedChange={() => toggleSectionVisibility('languages')}
                          />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={(structuredCv.languages || []).join(', ')}
                          onChange={(e) => updateField('languages', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="English (Native), Spanish (Fluent)..."
                          className="bg-gray-50 border-gray-200"
                          rows={2}
                        />
                      </CardContent>
                    </Card>

                    {/* Certifications */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-amber-600" />
                            Certifications & Training
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={sectionVisibility.certifications}
                              onCheckedChange={() => toggleSectionVisibility('certifications')}
                            />
                            <Button size="sm" variant="ghost" onClick={addCertification} data-testid="add-certification-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {(structuredCv.certifications || []).map((cert, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                            <div className="flex justify-between items-start gap-2">
                              <Input
                                value={cert.name || ''}
                                onChange={(e) => updateField(`certifications[${idx}].name`, e.target.value)}
                                placeholder="Certification Name"
                                className="bg-white border-gray-200 flex-1"
                                data-testid={`cert-name-${idx}`}
                              />
                              <Button size="sm" variant="ghost" onClick={() => removeCertification(idx)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={cert.issuer || ''}
                                onChange={(e) => updateField(`certifications[${idx}].issuer`, e.target.value)}
                                placeholder="Issuing Organization"
                                className="bg-white border-gray-200"
                                data-testid={`cert-issuer-${idx}`}
                              />
                              <Input
                                value={cert.year || ''}
                                onChange={(e) => updateField(`certifications[${idx}].year`, e.target.value)}
                                placeholder="Year"
                                className="bg-white border-gray-200"
                                data-testid={`cert-year-${idx}`}
                              />
                            </div>
                          </div>
                        ))}
                        {(!structuredCv.certifications || structuredCv.certifications.length === 0) && (
                          <p className="text-gray-400 text-sm text-center py-4">
                            No certifications. Click + to add one.
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Custom Sections */}
                    <Card className="bg-white border-gray-200 shadow-sm">
                      <CardHeader className="py-3">
                        <CardTitle className="text-gray-900 text-sm flex items-center justify-between">
                          Custom Sections
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={sectionVisibility.customSections}
                              onCheckedChange={() => toggleSectionVisibility('customSections')}
                            />
                            <Button size="sm" variant="ghost" onClick={addCustomSection} data-testid="add-custom-section-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {(structuredCv.customSections || []).map((section, sectionIdx) => (
                          <div key={sectionIdx} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                            <div className="flex justify-between items-start">
                              <Input
                                value={section.title || ''}
                                onChange={(e) => updateCustomSectionTitle(sectionIdx, e.target.value)}
                                placeholder="Section Title"
                                className="bg-white border-gray-200 font-medium"
                                data-testid={`custom-section-title-${sectionIdx}`}
                              />
                              <Button size="sm" variant="ghost" onClick={() => removeCustomSection(sectionIdx)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                            
                            <div className="mt-2">
                              <div className="flex justify-between items-center mb-2">
                                <Label className="text-xs text-gray-600">Items</Label>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => addCustomSectionItem(sectionIdx)}
                                  className="h-6 px-2"
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Add Item
                                </Button>
                              </div>
                              <div className="space-y-2">
                                {(section.items || []).map((item, itemIdx) => (
                                  <div key={itemIdx} className="flex gap-2">
                                    <span className="text-gray-400 mt-2">•</span>
                                    <Input
                                      value={item}
                                      onChange={(e) => updateCustomSectionItem(sectionIdx, itemIdx, e.target.value)}
                                      placeholder="Add item..."
                                      className="bg-white border-gray-200 flex-1"
                                    />
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => removeCustomSectionItem(sectionIdx, itemIdx)}
                                      className="h-9 px-2"
                                    >
                                      <X className="w-3 h-3 text-red-500" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                        {(!structuredCv.customSections || structuredCv.customSections.length === 0) && (
                          <p className="text-gray-400 text-sm text-center py-4">
                            No custom sections. Click + to add one.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>

              {/* Smart Edit Tab */}
              <TabsContent value="smart" className="p-4 space-y-4">
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-900 text-sm">Smart Edit</CardTitle>
                    <p className="text-gray-500 text-xs">Paste any content to automatically update your CV</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Textarea
                      value={smartEditContent}
                      onChange={(e) => setSmartEditContent(e.target.value)}
                      placeholder="Paste LinkedIn profile, old CV, job description, or any professional content..."
                      className="bg-gray-50 border-gray-200 min-h-[200px]"
                    />
                    
                    <div className="flex items-center gap-4">
                      <Label className="text-gray-700">Mode:</Label>
                      <Select value={smartEditMode} onValueChange={setSmartEditMode}>
                        <SelectTrigger className="w-[200px] bg-gray-50 border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="conservative">Conservative (Fill Blanks)</SelectItem>
                          <SelectItem value="replace">Replace Mode</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button 
                      onClick={handleSmartEdit}
                      disabled={smartEditLoading || !smartEditContent.trim()}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {smartEditLoading ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Apply Smart Edit</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Template Tab */}
              <TabsContent value="template" className="p-4 space-y-4">
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-900 text-sm">Select Template</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      {templates.map((template) => (
                        <div
                          key={template.id}
                          onClick={() => {
                            setSelectedTemplate(template.id);
                            if (cvId) {
                              v2API.update(cvId, {
                                v2TemplateId: template.id,
                                structuredCv,
                                language
                              }).then(() => {
                                loadCV();
                                toast.success('Template changed');
                              });
                            }
                          }}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedTemplate === template.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <p className="text-gray-900 font-medium text-sm">{template.name}</p>
                          <p className="text-gray-500 text-xs">
                            {template.hasPhoto ? 'Photo' : 'No Photo'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Cover Letter Tab */}
              <TabsContent value="cover" className="p-4 space-y-4">
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-900 text-sm">Generate Cover Letter</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-gray-700">Job Title *</Label>
                      <Input
                        value={coverLetterForm.jobTitle}
                        onChange={(e) => setCoverLetterForm(prev => ({ ...prev, jobTitle: e.target.value }))}
                        placeholder="Software Engineer"
                        className="bg-gray-50 border-gray-200"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Company Name</Label>
                      <Input
                        value={coverLetterForm.companyName}
                        onChange={(e) => setCoverLetterForm(prev => ({ ...prev, companyName: e.target.value }))}
                        placeholder="Google"
                        className="bg-gray-50 border-gray-200"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Job Description</Label>
                      <Textarea
                        value={coverLetterForm.jobDescription}
                        onChange={(e) => setCoverLetterForm(prev => ({ ...prev, jobDescription: e.target.value }))}
                        placeholder="Paste job description here..."
                        className="bg-gray-50 border-gray-200"
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Tone</Label>
                      <Select 
                        value={coverLetterForm.tone} 
                        onValueChange={(v) => setCoverLetterForm(prev => ({ ...prev, tone: v }))}
                      >
                        <SelectTrigger className="bg-gray-50 border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="warm">Warm</SelectItem>
                          <SelectItem value="direct">Direct</SelectItem>
                          <SelectItem value="executive">Executive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleGenerateCoverLetter}
                      disabled={generatingLetter || !coverLetterForm.jobTitle}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {generatingLetter ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" /> Generate Cover Letter</>
                      )}
                    </Button>
                    
                    {coverLetter && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-gray-900 font-medium mb-2">Generated Cover Letter</h4>
                        <p className="text-gray-700 text-sm whitespace-pre-wrap">{coverLetter.content}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Export Buttons */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-2">
              <Button
                onClick={() => window.open(v2API.exportPDFClean(cvId), '_blank')}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="export-pdf-clean"
              >
                <FileDown className="w-4 h-4 mr-2" /> PDF (Clean)
              </Button>
              <Button
                onClick={() => window.open(v2API.exportPDFWatermark(cvId), '_blank')}
                variant="outline"
                className="flex-1"
                data-testid="export-pdf-watermark"
              >
                <Download className="w-4 h-4 mr-2" /> PDF (Watermarked)
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 flex flex-col bg-gray-100">
          <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
            <h2 className="text-gray-900 font-medium">Live Preview</h2>
            <Button variant="ghost" size="sm" onClick={loadCV}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
          <div className="flex-1 overflow-auto p-6">
            {cv?.htmlSnapshot ? (
              <div className="max-w-[210mm] mx-auto shadow-2xl">
                <iframe
                  ref={iframeRef}
                  srcDoc={cv.htmlSnapshot}
                  className="w-full bg-white rounded-lg"
                  style={{ minHeight: '297mm' }}
                  title="CV Preview"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                {loading ? (
                  <><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading preview...</>
                ) : (
                  'No preview available'
                )}
              </div>
            )}
          </div>
        </div>

        {/* History Sidebar */}
        {showHistory && (
          <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-gray-900 font-medium">Update History</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {versions.map((version) => (
                <div key={version.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-900 text-sm font-medium">Version {version.versionNumber}</p>
                      <p className="text-gray-500 text-xs">{version.updateNote}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {new Date(version.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRestoreVersion(version.id)}
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {versions.length === 0 && (
                <p className="text-gray-400 text-sm text-center py-4">No history yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CVBuilderV2;
